import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { UploadSimple } from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CATS = [
  { slug: "feet", label: "Feet" }, { slug: "lingerie", label: "Lingerie" },
  { slug: "audio", label: "Fantasy Audio" }, { slug: "cosplay", label: "Cosplay" },
  { slug: "leather", label: "Leather & Latex" }, { slug: "voyeur", label: "Voyeur" },
  { slug: "general", label: "General" },
];

export default function Settings() {
  const { user, setUser, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({
      display_name: user.display_name || "",
      bio: user.bio || "",
      location: user.location || "",
      categories: user.categories || [],
      subscription_price: user.subscription_price || 0,
      avatar_id: user.avatar_id || null,
      banner_id: user.banner_id || null,
    });
  }, [user]);

  if (!form) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;
  const isCreator = user.role === "creator" || user.role === "admin";

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleCat = (slug) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug) ? f.categories.filter((c) => c !== slug) : [...f.categories, slug],
    }));
  };

  const uploadImage = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: data.id }));
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me", {
        ...form,
        subscription_price: Number(form.subscription_price),
      });
      setUser(data);
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 py-10" data-testid="settings-page">
      <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="font-display text-5xl md:text-6xl leading-none mb-8">Edit <span className="italic text-primary">profile</span></motion.h1>

      {/* banner + avatar */}
      <div className="relative mb-16">
        <div className="h-40 bg-card border border-border overflow-hidden">
          {form.banner_id && <img src={fileUrl(form.banner_id)} alt="" className="h-full w-full object-cover" />}
        </div>
        <label data-testid="banner-upload" className="absolute top-3 right-3 glass border border-white/20 px-3 py-1.5 text-xs font-accent uppercase tracking-widest cursor-pointer flex items-center gap-1.5">
          <UploadSimple size={14} /> Banner
          <input type="file" accept="image/*" hidden onChange={uploadImage("banner_id")} />
        </label>
        <label data-testid="avatar-upload" className="absolute -bottom-12 left-5 h-24 w-24 rounded-full border-4 border-background bg-card overflow-hidden flex items-center justify-center cursor-pointer">
          {form.avatar_id ? <img src={fileUrl(form.avatar_id)} alt="" className="h-full w-full object-cover" />
            : <UploadSimple size={22} className="text-primary" />}
          <input type="file" accept="image/*" hidden onChange={uploadImage("avatar_id")} />
        </label>
      </div>

      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Display name</label>
      <input data-testid="settings-name" value={form.display_name} onChange={upd("display_name")}
        className="w-full bg-card border border-border px-4 py-3 mb-5 focus:border-primary outline-none" />

      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Bio</label>
      <textarea data-testid="settings-bio" value={form.bio} onChange={upd("bio")} rows={3}
        className="w-full bg-card border border-border px-4 py-3 mb-5 focus:border-primary outline-none resize-none" />

      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Location</label>
      <input data-testid="settings-location" value={form.location} onChange={upd("location")}
        className="w-full bg-card border border-border px-4 py-3 mb-5 focus:border-primary outline-none" />

      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Categories / kinks</label>
      <div className="flex flex-wrap gap-2 mb-5">
        {CATS.map((c) => (
          <button key={c.slug} data-testid={`settings-cat-${c.slug}`} onClick={() => toggleCat(c.slug)}
            className={`px-3 py-1.5 border font-accent text-xs uppercase tracking-widest transition-colors ${form.categories.includes(c.slug) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {isCreator && (
        <>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Monthly subscription price ($)</label>
          <input data-testid="settings-subprice" type="number" min="0" step="0.5" value={form.subscription_price} onChange={upd("subscription_price")}
            className="w-full bg-card border border-border px-4 py-3 mb-5 focus:border-primary outline-none" />
        </>
      )}

      <button data-testid="settings-save" onClick={save} disabled={saving}
        className="px-8 py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm transition-transform active:scale-95 hover:bg-[#e6004c] disabled:opacity-60">
        {saving ? "Saving…" : "Save profile"}
      </button>
    </div>
  );
}
